import {
  getExternalOutflowAmount,
  getOpeningCapitalOutflowAmount,
  isSetupPhase,
  resolveLiquidOutflowPortions
} from './fundingSource';
import { collectUniqueSupplierPayments } from './supplierPaymentAggregation';
import { collectCustomerDebtPayments } from '../domain/customers/balance/debtCollection';
import { resolveSalePaymentMethodKey, PAYMENT_METHODS } from '../services/paymentMethodService';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout';

function emptyChannel() {
  return {
    income: 0,
    outgoing: 0,
    outgoingPayments: 0,
    outgoingExpenses: 0,
    externalFinancing: 0,
    openingCapitalFinancing: 0,
    balance: 0,
    financingExposure: 0,
    financingExposureExplicit: 0,
    financingExposureDeficit: 0
  };
}

/**
 * Financement externe = montants tagués hors caisse uniquement.
 * Le solde négatif (liquidityDeficit) reste dans la colonne solde.
 */
export function computeFinancingExposure({ balance = 0, externalFinancing = 0 } = {}) {
  const explicit = Math.max(0, Number(externalFinancing) || 0);
  const liquidityDeficit = Math.max(0, -(Number(balance) || 0));
  return {
    explicit,
    liquidityDeficit,
    total: explicit
  };
}

/**
 * Soldes Bilan (Espèces / Banque) — alignés sur le dashboard manager.
 * Banque = somme Bankily + Sedad + Massrivi + … (computeLiquidBalanceByMethod.electronic.balance).
 */
export function resolveBilanLiquidBalances({
  sales = [],
  expenses = [],
  supermarketPayments = [],
  invoicePayments = [],
  supermarketInvoices = [],
  supplierPayments = null,
  customerDebts = null,
  customerDebtPayments = null,
  installationPhase = null
} = {}) {
  const resolvedPayments =
    supplierPayments ??
    collectUniqueSupplierPayments({
      supermarketPayments,
      invoicePayments,
      supermarketInvoices
    });

  const liquid = computeLiquidBalanceByMethod({
    sales,
    expenses,
    supplierPayments: resolvedPayments,
    customerDebts,
    customerDebtPayments,
    installationPhase
  });

  return {
    cash: liquid.cash.balance,
    bank: liquid.electronic.balance,
    electronic: liquid.electronic.balance,
    total: liquid.total.balance,
    liquid
  };
}

function finalizeChannel(channel) {
  channel.balance = channel.income - channel.outgoing;
  const exposure = computeFinancingExposure(channel);
  channel.financingExposure = exposure.total;
  channel.financingExposureExplicit = exposure.explicit;
  channel.financingExposureDeficit = exposure.liquidityDeficit;
  return channel;
}

export function resolveOutflowMethodKey(entry) {
  const pm = String(entry?.paymentMethod || 'cash').trim().toLowerCase();
  if (pm === 'cash' || pm === 'espèces' || pm === 'especes') return 'cash';

  const provider = String(entry?.electronicPaymentMethod || entry?.payment?.electronicPaymentMethod || '')
    .trim()
    .toLowerCase();
  if (provider && PAYMENT_METHODS[provider]) return provider;
  if (PAYMENT_METHODS[pm]) return pm;
  if (['electronic', 'électronique', 'card', 'mobile'].includes(pm)) {
    return provider || 'bankily';
  }
  return provider || pm || 'bankily';
}

function addToChannel(channel, source) {
  channel.income += source.income || 0;
  channel.outgoing += source.outgoing || 0;
  channel.outgoingPayments += source.outgoingPayments || 0;
  channel.outgoingExpenses += source.outgoingExpenses || 0;
  channel.externalFinancing += source.externalFinancing || 0;
  channel.openingCapitalFinancing += source.openingCapitalFinancing || 0;
  finalizeChannel(channel);
}

/**
 * Liquid balances per payment method (espèces, Bankily, Sedad, …) + aggregates.
 * Sorties = paiements fournisseurs + dépenses (registerPortion uniquement).
 *
 * @param {object} args
 * @param {boolean} [args.installationPhase] — if set, overrides auto-detect from `sales`.
 *   Pass from FULL business sales when `sales` is period-filtered (dashboard / rapport).
 */
export function computeLiquidBalanceByMethod({
  sales = [],
  expenses = [],
  supermarketPayments = [],
  invoicePayments = [],
  supermarketInvoices = [],
  supplierPayments = null,
  customerDebts = null,
  customerDebtPayments = null,
  installationPhase = null
} = {}) {
  const methods = {};
  const ensure = (key) => {
    if (!methods[key]) methods[key] = emptyChannel();
    return methods[key];
  };

  (sales || []).forEach((sale) => {
    const income = resolveSaleRegisterIncome(sale);
    if (income.amount <= 0) return;
    const methodKey =
      income.channel === 'cash'
        ? 'cash'
        : resolveSalePaymentMethodKey({
            ...sale,
            unpaidAmount: 0,
            creditAmount: 0
          });
    ensure(methodKey).income += income.amount;
  });

  // Créance-Einzug (spätere Kundenzahlungen) → Kassen-/Electronic-Income
  const debtPayments =
    customerDebtPayments ??
    (customerDebts != null ? collectCustomerDebtPayments(customerDebts) : []);
  (debtPayments || []).forEach((payment) => {
    const amount = Math.max(0, Number(payment?.amount ?? payment?.totalAmount) || 0);
    if (amount <= 0) return;
    const methodKey = resolveOutflowMethodKey(payment);
    ensure(methodKey).income += amount;
  });

  // Installation = no register income yet — untagged legacy outflows → Startkapital (not Externe)
  const forceOpeningCapitalLegacy =
    installationPhase != null ? Boolean(installationPhase) : isSetupPhase({ sales });

  const applyOutflow = (entry, source) => {
    const allocations = Array.isArray(entry?.methodAllocations) ? entry.methodAllocations : null;
    const openingAmt = getOpeningCapitalOutflowAmount(entry);
    const externalAmtTagged = getExternalOutflowAmount(entry);

    // Multi-method split: book each register portion on its own channel
    if (allocations?.length > 0 && openingAmt <= 0 && externalAmtTagged <= 0 && !forceOpeningCapitalLegacy) {
      allocations.forEach((row) => {
        const key = row?.methodKey
          ? String(row.methodKey).trim().toLowerCase()
          : resolveOutflowMethodKey({
            paymentMethod: row?.paymentMethod || entry?.paymentMethod,
            electronicPaymentMethod: row?.electronicPaymentMethod || entry?.electronicPaymentMethod
          });
        const registerAmt = Math.max(0, Number(row?.registerPortion) || 0);
        if (registerAmt <= 0) return;
        const channel = ensure(key || 'cash');
        channel.outgoing += registerAmt;
        if (source === 'payment') channel.outgoingPayments += registerAmt;
        else channel.outgoingExpenses += registerAmt;
        finalizeChannel(channel);
      });

      const externalAmt = getExternalOutflowAmount(entry);
      if (externalAmt > 0) {
        const key = resolveOutflowMethodKey(entry);
        const channel = ensure(key);
        channel.externalFinancing += externalAmt;
        finalizeChannel(channel);
      }
      const openingSplit = getOpeningCapitalOutflowAmount(entry);
      if (openingSplit > 0) {
        const key = resolveOutflowMethodKey(entry);
        const channel = ensure(key);
        channel.openingCapitalFinancing += openingSplit;
        finalizeChannel(channel);
      }
      return;
    }

    const key = resolveOutflowMethodKey(entry);
    const { registerAmt, externalAmt, openingCapitalAmt } = resolveLiquidOutflowPortions(entry, {
      installationPhase: forceOpeningCapitalLegacy
    });

    const channel = ensure(key);
    if (registerAmt > 0) {
      channel.outgoing += registerAmt;
      if (source === 'payment') channel.outgoingPayments += registerAmt;
      else channel.outgoingExpenses += registerAmt;
    }
    if (externalAmt > 0) channel.externalFinancing += externalAmt;
    if (openingCapitalAmt > 0) channel.openingCapitalFinancing += openingCapitalAmt;
    finalizeChannel(channel);
  };

  const payments =
    supplierPayments ??
    collectUniqueSupplierPayments({
      supermarketPayments,
      invoicePayments,
      supermarketInvoices
    });

  (payments || []).forEach((entry) => applyOutflow(entry, 'payment'));
  (expenses || []).forEach((entry) => applyOutflow(entry, 'expense'));

  Object.keys(methods).forEach((key) => {
    finalizeChannel(methods[key]);
  });

  const cash = methods.cash ? { ...methods.cash } : emptyChannel();
  const electronic = emptyChannel();
  const total = emptyChannel();

  Object.entries(methods).forEach(([key, channel]) => {
    addToChannel(total, channel);
    if (key !== 'cash') addToChannel(electronic, channel);
  });

  return { methods, cash, electronic, total };
}

/**
 * Liquid balances per payment channel (cash vs electronic).
 */
export function computeLiquidBalanceByChannel(args = {}) {
  const detailed = computeLiquidBalanceByMethod(args);
  return {
    cash: detailed.cash,
    electronic: detailed.electronic,
    total: detailed.total,
    methods: detailed.methods
  };
}
